import { format, startOfMonth, endOfMonth, subMonths } from "date-fns"

// ---------- dates ----------
export const ymd = d => format(new Date(d), "yyyy-MM-dd")

// ---------- plăcuțe înmatriculare ----------
// Normalizează un număr de înmatriculare pentru comparație: fără spații, litere
// mici. Așa „b777", „b 777", „B 777 TST" se potrivesc toate pe aceeași mașină —
// altfel căutarea cu/fără spații rata mașina existentă și se crea duplicat.
export const normalizePlate = s => (s || "").toString().replace(/\s+/g, "").toLowerCase()

// ---------- files ----------
// Slug sigur pentru nume de fișier: fără diacritice, litere mici, cuvinte
// separate prin „-" (ex: "Auto Service SRL" -> "auto-service-srl").
export const fileSlug = text => (text || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "")

// Nume descriptiv pentru PDF-ul unei facturi: factura-<firma>-<data>.pdf
// (ex: factura-auto-service-srl-2026-10-08.pdf). Întoarce "" dacă lipsește
// firma sau data, lăsând apelantul să cadă pe alt fallback.
export function invoicePdfFilename (company, date) {
  const slug = fileSlug(company)
  const d = date ? ymd(date) : ""
  return slug && d ? `factura-${slug}-${d}.pdf` : ""
}

// Extrage numele fișierului din header-ul Content-Disposition trimis de BE
// (ex: `attachment; filename="Factura_ASP0022_..._LUNGU-TRANS-SRL.pdf"`).
// Întoarce "" dacă header-ul lipsește sau n-are filename.
export function filenameFromContentDisposition (headers) {
  const cd = (headers && (headers["content-disposition"] || headers["Content-Disposition"])) || ""
  const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
  return match && match[1] ? decodeURIComponent(match[1]) : ""
}

// "yyyy-MM-dd" -> "marți, 16 iunie 2026", using the app's lang.days/lang.months arrays.
// { withYear: false } drops the trailing year, e.g. for toast messages
// where the year is implied ("sâmbătă, 29 august").
export function formatDayLabel (iso, lang, { withYear = true } = {}) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '')
  if (!m) return ''
  const year = +m[1]
  const month = +m[2] - 1
  const day = +m[3]
  const dow = new Date(year, month, day).getDay()
  const dayName = lang.days[dow === 0 ? 6 : dow - 1]
  return withYear
    ? `${dayName}, ${day} ${lang.months[month]} ${year}`
    : `${dayName}, ${day} ${lang.months[month]}`
}

// { year, month (0-indexed), day } -> "yyyy-MM-dd"
export function isoDateForItem (item) {
  return `${item.year}-${String(item.month + 1).padStart(2, '0')}-${String(item.day).padStart(2, '0')}`
}

// "yyyy-MM-dd" -> true when strictly before today (calendar-day comparison,
// ignoring time-of-day) — zero-padded ISO strings sort correctly as text.
export function isPastIsoDate (iso) {
  return iso < ymd(new Date())
}

export const monthBounds = preset => {
  const base = preset === "this" ? new Date() : subMonths(new Date(), 1)
  return { start: startOfMonth(base), end: endOfMonth(base) }
}

export const isMonth = (range, preset) => {
  if (!range || !range.start || !range.end) return false
  const b = monthBounds(preset)
  return ymd(range.start) === ymd(b.start) && ymd(range.end) === ymd(b.end)
}

export function getEstimatedDate (days = 7) {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return `<div>${format(date, "dd.MM.yyyy")}</div>`
}

// ---------- strings ----------
export const norm = s => (s || "").toLowerCase().replace(/\s+/g, "")

// ---------- validare formular "Mașină nouă în service" (IntrareNouaV2) ----------
// Logică pură de validare/normalizare, independentă de layout (vezi
// tests/unit/intrare_noua_v2_validators.spec.js și cypress/fail-cases/IntrareNouaV2.md).

// Gol după trim: null/undefined/doar spații-tab-newline (REQ-02 / SEC-01).
export const isBlank = v => v == null || String(v).trim() === ''

// Număr auto: uppercase + trim capete + colapsează spațiile interioare la unul
// singur, păstrând forma introdusă (cu sau fără spații) — FMT-01.
export const normalizeNumarAuto = s => (s || '').toUpperCase().trim().replace(/\s+/g, ' ')

// Format RO: 1-2 litere județ + 2-3 cifre + 3 litere, cu sau fără spații, orice case.
export function isValidNumarAuto (s) {
  const compact = (s || '').toUpperCase().replace(/\s+/g, '')
  return /^[A-Z]{1,2}\d{2,3}[A-Z]{3}$/.test(compact)
}

// VIN/serie șasiu: uppercase + elimină toate spațiile — FMT-02 / FMT-03.
export const normalizeVin = s => (s || '').toUpperCase().replace(/\s+/g, '')

// VIN standard = 17 caractere, fără I/O/Q. Întoarce { valid, errors }.
export function validateVin (s) {
  const vin = normalizeVin(s)
  const errors = []
  if (vin.length !== 17) errors.push('length')
  if (/[IOQ]/.test(vin)) errors.push('forbidden_chars')
  return { valid: errors.length === 0, errors }
}

// Km: doar întreg >= 0. Respinge text, notație științifică, zecimale,
// separatori de mii/spații și negative (FMT-06 / BND-04). Întoarce null dacă invalid.
export function parseKm (s) {
  if (s == null) return null
  const str = String(s).trim()
  return /^\d+$/.test(str) ? parseInt(str, 10) : null
}

export const isValidKm = s => parseKm(s) !== null

// Câmpurile obligatorii pentru salvare (fail-cases Q2), în ordinea de focus:
// pe primul invalid se face scroll/focus. `scope` = obiectul formular (car/client).
// Mesajul pentru un VIN completat dar invalid (chars interzise vs lungime).
export function vinInvalidMessage (s) {
  const { errors } = validateVin(s)
  const reason = errors.includes('forbidden_chars')
    ? 'VIN-ul conține caractere invalide (O, I, Q nu sunt permise)'
    : errors.includes('length')
      ? 'VIN-ul trebuie să aibă 17 caractere'
      : 'VIN invalid'
  return `${reason}. Din această cauză devizul nu va putea fi înregistrat în RAR AutoPass.`
}

// Validarea e pe DOUĂ tiere, după feedback-ul din service-uri („lasă-mă să salvez
// cum vreau eu"):
//   1. REQUIRED_FIELDS → GOLUL blochează salvarea. Doar completare, fără format.
//   2. FORMAT_NOTICES  → format neobișnuit = notice advisory, NU blochează.
// Așa numărul/km/VIN pot fi salvate chiar dacă formatul nu e canonic — doar
// atenționăm (soft-notice), nu refuzăm.

// Câmpuri obligatorii: goale → border roșu + blochează salvarea (fără mesaj).
export const REQUIRED_FIELDS = [
  { key: 'numar', scope: 'car' },
  { key: 'km', scope: 'car' },
  { key: 'nume', scope: 'client' }
]

// Întoarce { numar?, km?, nume? } cu { type: 'required' } pentru fiecare câmp gol.
// Gol (fără chei) = valid pentru salvare.
export function getRequiredErrors (car, client) {
  const scopes = { car, client }
  const errors = {}
  REQUIRED_FIELDS.forEach(({ key, scope }) => {
    if (isBlank(scopes[scope][key])) errors[key] = { type: 'required' }
  })
  return errors
}

// Câmpuri cu notice de format (advisory, NU blochează). `message` = string sau
// fn(value). Se evaluează DOAR când câmpul e completat; gol = fără notice.
// Deliberat DOAR VIN-ul: feedback on-spot sub câmp, fără a încărca UI-ul cu
// notice-uri pe fiecare câmp (număr/km rămân fără semnalare de format).
export const FORMAT_NOTICES = [
  { key: 'sasiu', valid: s => validateVin(s).valid, message: vinInvalidMessage }
]

// Întoarce { sasiu? } → string de notice pentru câmpul auto completat dar cu
// format neobișnuit. Gol (fără chei) = nimic de semnalat.
export function getFormatNotices (car) {
  const notices = {}
  FORMAT_NOTICES.forEach(({ key, valid, message }) => {
    const value = car[key]
    if (isBlank(value)) return
    if (!valid(value)) notices[key] = typeof message === 'function' ? message(value) : message
  })
  return notices
}

// Construiește payload-ul trimis la BE la salvare, normalizând câmpurile cheie
// (număr/VIN/km) și făcând trim pe nume. Pur: nu atinge starea componentei.
// Câmpurile cerinte_client / defectiuni / piese_client sunt stocate în BE ca
// HTML (moștenire din editarea contenteditable veche) și randate ca HTML în
// Deviz/Comandă. În formular le edităm ca text simplu în <textarea>, deci
// convertim HTML↔text la încărcare/salvare.
export function htmlToText (html) {
  if (!html) return ''
  const withBreaks = html
    .replace(/<div>\s*<br\s*\/?>\s*<\/div>/gi, '\n')
    .replace(/<div>|<br\s*\/?>/gi, '\n')
    .replace(/<\/div>/gi, '')
  const el = document.createElement('div')
  el.innerHTML = withBreaks
  const text = el.textContent
  return text.charAt(0) === '\n' ? text.slice(1) : text
}

export function textToHtml (text) {
  if (!text) return ''
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>')
}

// Normalizare defensivă a răspunsului de la `in2_load_comanda`: garantează forma
// (car/client/comanda = obiecte, documents = array, dosar_dauna = bool,
// partslink24Url = string), ca un răspuns malformat să nu umple prost formularul
// sau să arunce. (DFF-A5)
export function normalizeLoadedComanda (data) {
  const d = data && typeof data === 'object' ? data : {}
  const asObj = v => (v && typeof v === 'object' && !Array.isArray(v) ? v : {})
  const car = asObj(d.car)
  return {
    car,
    client: asObj(d.client),
    comanda: asObj(d.comanda),
    documents: Array.isArray(d.documents) ? d.documents : [],
    dosar_dauna: d.dosar_dauna === true,
    partslink24Url: typeof car.partslink24_url === 'string' ? car.partslink24_url : ''
  }
}

export function buildComandaPayload ({ car, client, comanda, baseComanda = {}, dosar_dauna, statusId, mechanicId, rampId }) {
  return {
    car: {
      ...car,
      numar: normalizeNumarAuto(car.numar),
      sasiu: normalizeVin(car.sasiu),
      km: parseKm(car.km)
    },
    client: { ...client, nume: (client.nume || '').trim() },
    // Pornim de la subobiectul `comanda` existent (păstrează date, termen_estimativ,
    // nota, etc. la editare) și suprascriem doar câmpurile editate în formular.
    // Text simplu din textarea → HTML, ca să rămână consistent cu formatul BE.
    // `date` lipsește la creare → îl setăm acum (ca în fluxul vechi).
    comanda: {
      ...baseComanda,
      cerinte_client: textToHtml(comanda.cerinte_client),
      defectiuni: textToHtml(comanda.defectiuni),
      piese_client: textToHtml(comanda.piese_client),
      observatii: textToHtml(comanda.observatii),
      date: baseComanda.date || new Date()
    },
    dosar_dauna,
    statusId,
    mechanicId,
    rampId,
    status: 0
  }
}

// Ruta devizului pentru o comandă, în funcție de tipul de deviz din setări.
// Aceeași logică ca `Handle_Deviz_Route` din IntrareNoua (vechi) — folosită
// atât de bara desktop (IntrareActionBar), cât și de meniul „Opțiuni" mobil.
export function devizRouteFor (settings, id) {
  switch (settings && settings.deviz) {
    case 'simple': return `/deviz-simplu/${id}`
    case 'invoice': return `/deviz-invoice/${id}`
    case 'auto':
    case 'model_1': return `/deviz-auto/${id}`
    case 'model_3': return `/deviz-model-3/${id}`
    case 'complex':
    default: return `/deviz/${id}`
  }
}

// ---------- query params ----------
export const buildDateRangeParams = ({ dateRange, rar }) => ({
  ...(dateRange && dateRange.start && { from: ymd(dateRange.start) }),
  ...(dateRange && dateRange.end   && { to:   ymd(dateRange.end) }),
  ...(rar                          && { rar })
})

// ---------- calendar grid ----------
/**
 * Monday-start month grid: array of 7-cell weeks spanning the given month,
 * padded with adjacent-month days at both ends.
 * Each cell: { day: Number, monthType: 'prev' | 'current' | 'next' }.
 */
export function buildMonthWeeks (year, month) {
  const weeks = [];

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);

  let startDay = firstDayOfMonth.getDay();
  startDay = startDay === 0 ? 6 : startDay - 1;

  const daysInMonth = lastDayOfMonth.getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  let currentWeek = [];

  for (let i = startDay - 1; i >= 0; i--) {
    currentWeek.push({
      day: daysInPrevMonth - i,
      monthType: 'prev'
    });
  }

  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push({
      day,
      monthType: 'current'
    });

    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  if (currentWeek.length > 0) {
    let nextDay = 1;

    while (currentWeek.length < 7) {
      currentWeek.push({
        day: nextDay++,
        monthType: 'next'
      });
    }

    weeks.push(currentWeek);
  }

  return weeks;
}

export function resolveCellMonth (currentMonth, monthType) {
  if (monthType === 'prev') return currentMonth === 0 ? 11 : currentMonth - 1;
  if (monthType === 'next') return currentMonth === 11 ? 0 : currentMonth + 1;
  return currentMonth;
}

// { day, monthType } grid cell + the calendar's current month/year -> "yyyy-MM-dd"
export function isoDateForCell (currentMonth, currentYear, date) {
  const month = resolveCellMonth(currentMonth, date.monthType);
  let year = currentYear;
  if (date.monthType === 'prev' && currentMonth === 0) year -= 1;
  else if (date.monthType === 'next' && currentMonth === 11) year += 1;
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
}

// ---------- number parsing ----------
/**
 * Parse a value into a finite number, accepting `,` or `.` as decimal separator.
 * Returns 0 for null / undefined / empty / non-numeric input.
 */
export function _Parse_Number (value) {
  if (value === undefined || value === null || value === '') return 0
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  const n = parseFloat(String(value).replace(',', '.'))
  return Number.isFinite(n) ? n : 0
}

// ---------- meta ads attribution ----------
// Reads fbclid/fbp/fbc off a /comenzi redirect URL coming from the marketing
// site and returns the _fbp/_fbc cookie values to persist on this subdomain,
// so the pixel here reuses the click/browser ids already attributed there.
// Only returns keys for params actually present, so callers never clobber an
// existing cookie with a blank value on a normal (non-ad) navigation.
export function resolveMetaAdCookies (search, now = Date.now()) {
  const params = new URLSearchParams(search || '')
  const fbclid = params.get('fbclid')
  const fbp = params.get('fbp')
  const fbc = params.get('fbc') || (fbclid ? `fb.1.${now}.${fbclid}` : null)

  const cookies = {}
  if (fbp) cookies._fbp = fbp
  if (fbc) cookies._fbc = fbc
  return cookies
}
